import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import { connect } from 'react-redux';
import { Button, FormGroup, ControlLabel, FormControl, HelpBlock, Alert } from 'react-bootstrap';
import { Field, reduxForm, formValueSelector, SubmissionError } from 'redux-form';
import Toggle from 'react-bootstrap-toggle';
import Joi from 'joi';
import { addContact, addAccountContact } from './actions';
import CRMInstanceName from '../crm-instance-name';

// --- Legacy constraints for validate.js (now replaced by Joi schema below) ---
// export const constraints = {
//   first_name: {
//     presence: true, length: { maximum: 200, minimum: 1 },
//   },
//   last_name: {
//     presence: true, length: { maximum: 200, minimum: 1 },
//   },
//   title: {
//     length: { maximum: 200 },
//   },
//   email: {
//     presence: true, email: true, length: { maximum: 200 },
//   },
//   phone: { length: { maximum: 20 } },
//   mobile_phone: { length: { maximum: 20 } },
//   description: { length: { maximum: 2048 } },
// };
// --- End legacy constraints ---

class ContactEditor extends Component {
  constructor() {
    super();
    this.state = {
      showError: false,
    };
    this.submit = this.submit.bind(this);
    this.renderField = this.renderField.bind(this);
    this.onTogglePrimary = this.onTogglePrimary.bind(this);
    this.handleDismissAlert = this.handleDismissAlert.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.shouldReset && nextProps.shouldReset) {
      this.props.initialize({});
      this.props.resetForm();
    }
  }

  onTogglePrimary() {
    const { isPrimary } = this.props;
    this.props.change('is_primary', !isPrimary);
  }

  handleDismissAlert() {
    this.setState({ showError: false });
  }

  submit(data) {
    const { opportunity, account, isAccount } = this.props;
    let id = null;
    if (isAccount && account && account.account_id) {
      id = account.account_id;
    } else if (opportunity && opportunity.opportunity_id) {
      id = opportunity.opportunity_id;
    }
    if (id) {
      const params = { is_primary: false, ...data };
      const action = isAccount ? addAccountContact : addContact;
      return this.props.dispatch(action(id, params))
        .then(
          (response) => {
            // addContact returns the contact object directly, while addAccountContact returns an object with a contact property
            const contact = isAccount ? response : response.contact;
            const role = isAccount ? null : response.role;
            const error = isAccount ? null : response.error;
            this.props.onContactAdd(contact, error);
            this.props.resetForm();
          },
          (error) => {
            const e = error.data || error;
            if (e.message) {
              e._error = e.message;
            }
            this.setState({ showError: true });
            throw new SubmissionError(e);
          },
        );
    }

    this.setState({ showError: true });
    const message = isAccount ? 'Missing Account' : 'Missing Opportunity';
    throw new SubmissionError({ _error: message });
  }

  renderField({
    input, label, componentClass, options, type, meta: { touched, error }, className,
  }) {
    const { submitting } = this.props;
    const validationState = touched && error ? 'error' : null;
    return (
      <FormGroup className={className} validationState={validationState}>
        <ControlLabel>{label}</ControlLabel>
        {componentClass !== 'select' &&
          <FormControl
            {...input}
            componentClass={componentClass}
            placeholder={label}
            type={type}
            disabled={submitting}
          />
        }
        {componentClass === 'select' &&
          <FormControl
            {...input}
            componentClass={componentClass}
            type={type}
            disabled={submitting}
          >
            <option />
            {options.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </FormControl>
        }
        {error && touched && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderEmpty() {
    return (
      <div className="alert alert-warning"><Fa name="arrow-left" /> Please select a contact</div>
    );
  }

  render() {
    const {
      handleSubmit, submitting, opportunity, account,
      invalid, roles, isPrimary, error, isAccount, canMakeContactPrimary,
    } = this.props;
    const { showError } = this.state;

    return (
      <div>
        <form
          onSubmit={handleSubmit(this.submit)}
        >
          <div className="alert alert-warning">
            <Fa name="exclamation-triangle" /> This will add a contact to your <CRMInstanceName />.<br />
            {isAccount && <div>Make sure you have sufficient permissions to create a contact.</div>}
            {!isAccount && <div>Make sure you have sufficient permissions to create a contact and to add a contact role to an opportunity.</div>}
          </div>
          {((opportunity !== null && opportunity !== undefined) || (account !== null && account !== undefined)) &&
            <div className="well well-sm">
              {opportunity !== null && opportunity !== undefined &&
                <FormGroup>
                  <ControlLabel>Opportunity</ControlLabel>
                  <FormControl
                    value={opportunity.name}
                    disabled
                    readOnly
                  />
                </FormGroup>
              }
              {account !== null && account !== undefined &&
                <FormGroup>
                  <ControlLabel>Account</ControlLabel>
                  <FormControl
                    value={account.name}
                    disabled
                    readOnly
                  />
                </FormGroup>
              }
            </div>
          }

          <div className="row">
            <Field name="first_name" type="text" component={this.renderField} label="First Name" className="col-xs-6" />
            <Field name="last_name" type="text" component={this.renderField} label="Last Name" className="col-xs-6" />
          </div>
          <div className="row">
            <Field name="title" type="text" component={this.renderField} label="Title" className="col-xs-6" />
            <Field name="email" type="email" component={this.renderField} label="Email" className="col-xs-6" />
          </div>
          <div className="row">
            <Field name="phone" type="text" component={this.renderField} label="Phone" className="col-xs-6" />
            <Field name="mobile_phone" type="text" component={this.renderField} label="Mobile" className="col-xs-6" />
          </div>

          {!isAccount &&
            <div className="row">
              <Field name="role" componentClass="select" options={roles} component={this.renderField} label="Opportunity Role" className="col-xs-6" />
              {canMakeContactPrimary && <FormGroup className="col-xs-6">
                <ControlLabel>Is Opportunity Primary Contact:</ControlLabel>
                <div>
                  <Toggle
                    onClick={this.onTogglePrimary}
                    on={<span>YES</span>}
                    off={<span>No</span>}
                    size="xs"
                    onstyle="success"
                    offstyle="danger"
                    active={isPrimary}
                    disabled={submitting}
                  />
                </div>
              </FormGroup>}
            </div>
          }

          <Field name="description" componentClass="textarea" component={this.renderField} label="Description" />
          <hr />
          {error && showError &&
            <Alert bsStyle="danger" onDismiss={this.handleDismissAlert}>
              {error}
            </Alert>
          }
          <div className="text-right">
            <Button bsStyle="primary" type="submit" disabled={submitting || invalid}>
              {!submitting && <span>Add Contact</span>}
              {submitting && <span><Fa name="spinner" spin /> Adding Contact</span>}
            </Button>
          </div>
        </form>
      </div>
    );
  }
}

ContactEditor.propTypes = {
  dispatch: PropTypes.func.isRequired,
  opportunity: PropTypes.shape({
    opportunity_id: PropTypes.string,
    name: PropTypes.string,
  }),
  account: PropTypes.shape({
    account_id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  isAccount: PropTypes.bool,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  initialize: PropTypes.func,
  shouldReset: PropTypes.bool,
  resetForm: PropTypes.func,
  invalid: PropTypes.bool,
  change: PropTypes.func,
  roles: PropTypes.arrayOf(PropTypes.shape({})),
  isPrimary: PropTypes.bool,
  error: PropTypes.string,
  onContactAdd: PropTypes.func,
  canMakeContactPrimary: PropTypes.bool,
};
ContactEditor.defaultProps = {
  submitting: false,
  opportunity: null,
  isAccount: false,
  handleSubmit: () => {},
  initialize: () => {},
  resetForm: () => {},
  change: () => {},
  shouldReset: false,
  invalid: false,
  roles: [],
  isPrimary: false,
  error: null,
  onContactAdd: () => {},
  canMakeContactPrimary: false,
};

const selector = formValueSelector('opportunityContactForm'); // <-- same as form name

function validate(values) {
  const schema = Joi.object({
    first_name: Joi.string().min(1).max(200).required().messages({
      'string.base': 'First name must be a string',
      'string.empty': 'First name is required',
      'any.required': 'First name is required',
      'string.min': 'First name is not allowed to be empty',
      'string.max': 'First name must be at most 200 characters',
    }),
    last_name: Joi.string().min(1).max(200).required().messages({
      'string.base': 'Last name must be a string',
      'string.empty': 'Last name is required',
      'any.required': 'Last name is required',
      'string.min': 'Last name is not allowed to be empty',
      'string.max': 'Last name must be at most 200 characters',
    }),
    title: Joi.string().max(200).allow('', null).messages({
      'string.base': 'Title must be a string',
      'string.max': 'Title must be at most 200 characters',
    }),
    email: Joi.string().email({ tlds: { allow: false } }).max(200).required().messages({
      'string.base': 'Email must be a string',
      'string.empty': 'Email is required',
      'any.required': 'Email is required',
      'string.email': 'Email must be a valid email address',
      'string.max': 'Email must be at most 200 characters',
    }),
    phone: Joi.string().max(20).allow('', null).messages({
      'string.base': 'Phone must be a string',
      'string.max': 'Phone must be at most 20 characters',
    }),
    mobile_phone: Joi.string().max(20).allow('', null).messages({
      'string.base': 'Mobile phone must be a string',
      'string.max': 'Mobile phone must be at most 20 characters',
    }),
    description: Joi.string().max(2048).allow('', null).messages({
      'string.base': 'Description must be a string',
      'string.max': 'Description must be at most 2048 characters',
    }),
  }).unknown(true);
  const { error } = schema.validate(values, { abortEarly: false });
  if (!error) return {};
  const errors = {};
  error.details.forEach((err) => {
    // Map Joi's error path to redux-form's error object
    const key = err.path[0];
    errors[key] = err.message;
  });
  return errors;
}

const ContactEditorForm = reduxForm({
  form: 'opportunityContactForm',
  validate,
})(ContactEditor);

const mapStateToProps = state => ({
  isPrimary: selector(state, 'is_primary'),
});

export default connect(mapStateToProps)(ContactEditorForm);
